;(() => {
  const SECTION_CLASS = 'js-stylist-locator'
  const controllers = new Map()
  const namespace = globalThis.StylistLocator || {}
  const {
    ToastManager,
    loadGoogleMaps,
    map = {},
    search = {},
    filters = {},
    list = {},
    info = {},
    gallery = {},
    api = {},
    geometry = {},
    analytics = {},
  } = namespace

  if (!ToastManager || !loadGoogleMaps) {
    console.warn('Stylist Locator: missing shared utilities, aborting initialization')
    return
  }

  const {
    DEFAULT_CENTER,
    DEFAULT_ZOOM_OUT,
    createMap,
    updateMarkers,
    updateRadiusCircle,
    createUserMarker,
    centerMapOnLocation,
    initializeUserLocation,
    initializeClusterer,
  } = map

  const {
    createStarMesh,
    removeStarMesh,
  } = namespace.clusterMesh || {}

  const {
    setupSearchInputs,
    handlePlaceSelection,
    triggerGeocode,
    hideAutocompleteDropdown,
  } = search

  const {
    setupFilters,
    showPopover,
    setupRadiusSelect,
    hasActiveFilters,
    getRadiusLabel,
  } = filters

  const { renderList } = list
  const {
    getProfileSections,
    renderInfoWindowContent,
    renderDrawerContent,
    initializeReadMore,
  } = info

  const { initializeGalleries } = gallery
  const { loadStylists } = api
  const {
    filterStylistsByRadius,
    filterStylistsByValidRoles,
    filterStylistsByCertification,
    calculateZoomForRadius,
    computeBoundsFromCenter,
    computeDistanceMeters,
    determineRoleCategory,
  } = geometry

  class StylistLocatorController {
    constructor(root) {
      this.root = root
      this.sectionId = root.dataset.sectionId
      this.apiBaseDomain = root.dataset.apiEndpoint || ''
      this.googleMapsKey = root.dataset.googleMapsKey
      this.heading = root.dataset.heading || 'Stylist Locator'
      this.defaultRadius = root.dataset.defaultRadius || '25'

      this.state = {
        stylists: [],
        cachedStylists: [],
        isLoading: false,
        selectedStylist: null,
        search: '',
        certification: 'all',
        radius: null,
        radiusCircle: null,
        userCenter: null,
        searchCenter: null,
        showRequest: false,
        name: '',
        email: '',
        phone: '',
        howDidYouHear: '',
        windowWidth: window.innerWidth,
        map: null,
        mapContainer: null,
        markers: new Map(),
        infoWindows: new Map(),
        openedInfoWindow: null,
        geocoder: null,
        autocompleteService: null,
        placesService: null,
        userMarker: null,
        viewMode: 'map',
        referenceCenter: null,
        locationEnabled: false,
        initialRadius: this.defaultRadius,
        initialCertification: 'all',
        initialCenter: null,
        hasInitialized: false,
        radiusIsDefault: true,
        certificationIsDefault: true,
        autocompleteTimeout: null,
        isDropdownOpen: false,
        apiEndpoint: this.buildEndpoint('/api/search-customers-db'),
        markerClusterer: null,
        meshMarkers: null,
        meshMarkerMap: null,
        tempRemovedMarkers: null,
        isClusteringEnabled: null, // Track clustering state to prevent unnecessary marker updates (null = uninitialized)
        openPopover: null, // Track currently open popover
        openPopoverButton: null, // Track which button opened the current popover
      }

      this.refs = this.collectRefs()
      this.toastManager = new ToastManager(this.refs.toastContainer)
      this.galleryCleanups = new Set()
      this.boundResizeHandler = this.handleResize.bind(this)
      this.boundDocumentClick = null

      this.init()
    }

    buildEndpoint(path) {
      if (!this.apiBaseDomain) return ''
      const base = this.apiBaseDomain.replace(/\/$/, '')
      return `${base}${path}`
    }

    getRadiusMiles() {
      return Number(this.state.radius) || Number(this.defaultRadius) || 25
    }

    setSearchInputs(value) {
      this.refs.searchInputs.forEach((input) => {
        input.value = value
      })
    }

    centerMap(coords, radiusMiles = this.getRadiusMiles()) {
      if (!this.state.map || !coords) return
      const zoom = calculateZoomForRadius(
        radiusMiles,
        this.state.map,
        this.state.mapContainer,
        coords
      )
      centerMapOnLocation(this.state.map, coords, zoom)
    }

    scheduleMapResize(delay = 100) {
      if (!this.state.map) return
      setTimeout(() => {
        globalThis.google.maps.event.trigger(this.state.map, 'resize')
        this.updateRadiusCircle()
      }, delay)
    }

    clearMeshMarkers() {
      if (!this.state.meshMarkers) return
      if (removeStarMesh) {
        removeStarMesh(this.state.meshMarkers)
      }
      this.state.meshMarkers = null

      // Restore temporarily removed markers (both cluster and standalone)
      if (this.state.tempRemovedMarkers) {
        // Check if we're in clustering mode
        const shouldCluster = this.state.isClusteringEnabled === true ||
          (this.state.map && this.state.map.getZoom() < 24)

        if (shouldCluster && this.state.markerClusterer) {
          // In clustering mode, add markers back to clusterer
          this.state.markerClusterer.addMarkers(this.state.tempRemovedMarkers)
        } else {
          // Not clustering - restore markers directly to map
          this.state.tempRemovedMarkers.forEach((marker) => {
            if (marker && this.state.map) {
              marker.setMap(this.state.map)
            }
          })
        }
        this.state.tempRemovedMarkers = null
      }
    }

    applySearchCenter(coords, searchValue = null) {
      if (searchValue !== null) {
        this.setSearchInputs(searchValue)
        this.state.search = searchValue
      }
      this.state.searchCenter = coords
      this.state.referenceCenter = coords

      this.centerMap(coords)
      this.refreshResults()
    }

    renderListAndMarkers() {
      this.renderList()
      this.updateMarkers()
    }

    setStylists(stylists, { render = true } = {}) {
      this.state.stylists = stylists
      if (render) {
        this.renderListAndMarkers()
      }
    }

    clearSelection() {
      this.state.selectedStylist = null
      this.state.showRequest = false
    }

    closeDrawer() {
      if (this.refs.drawer) {
        this.refs.drawer.classList.remove('stylist-locator__drawer--open')
      }
    }

    closeInfoWindow() {
      if (this.state.openedInfoWindow) {
        this.state.openedInfoWindow.close()
        this.state.openedInfoWindow = null
      }
    }

    resetSelectionUI({
      closeDrawer = false,
      closeInfoWindow = false,
      renderList = true,
      renderDrawer = true,
    } = {}) {
      this.clearSelection()
      if (closeInfoWindow) {
        this.closeInfoWindow()
      }
      if (closeDrawer) {
        this.closeDrawer()
      }
      if (renderList) {
        this.renderList()
      }
      if (renderDrawer) {
        this.renderDrawer()
      }
    }

    showRequestError(message) {
      this.toastManager.show('request', {
        message,
        severity: 'error',
        duration: 3000,
      })
    }

    refreshResults({ updateResetButtons = true } = {}) {
      this.updateRadiusCircle()
      this.fetchStylistsForCurrentRadius()
      this.applyFilters()
      if (updateResetButtons) {
        this.updateResetButtons()
      }
    }

    collectRefs() {
      return {
        heading: this.root.querySelector('[data-element="heading"]'),
        searchInputs: Array.from(this.root.querySelectorAll('[data-element="search-input"]')),
        searchButtons: Array.from(this.root.querySelectorAll('[data-element="search-button"]')),
        searchContainers: Array.from(this.root.querySelectorAll('[data-element="search-container"]')),
        autocompleteDropdowns: Array.from(this.root.querySelectorAll('[data-element="autocomplete-dropdown"]')),
        autocompleteContents: Array.from(this.root.querySelectorAll('[data-element="autocomplete-content"]')),
        certificationRadios: Array.from(this.root.querySelectorAll('[data-element="certification-radio"]')),
        filterInfoButtons: Array.from(this.root.querySelectorAll('[data-element="certification-info"]')),
        filterPopovers: Array.from(this.root.querySelectorAll('[data-element="certification-popover"]')),
        filterPopover: this.root.querySelector('[data-element="certification-popover"]'), // Keep for backward compatibility
        filterToggleButtons: Array.from(this.root.querySelectorAll('[data-element="filters-toggle"]')),
        filterDrawer: this.root.querySelector('[data-element="filters-drawer"]'),
        filterDrawerClose: this.root.querySelector('[data-element="filters-drawer-close"]'),
        listCount: this.root.querySelector('[data-element="list-count"]'),
        listCountMobile: this.root.querySelector('[data-element="list-count-mobile"]'),
        listDesktop: this.root.querySelector('[data-element="list-desktop"]'),
        listMobile: this.root.querySelector('[data-element="list-mobile"]'),
        viewToggleButtons: Array.from(this.root.querySelectorAll('[data-element="view-toggle-button"]')),
        mapHostDesktop: this.root.querySelector('[data-element="map-host-desktop"]'),
        mapHostMobile: this.root.querySelector('[data-element="map-host-mobile"]'),
        mobileLayout: this.root.querySelector('[data-element="mobile-layout"]'),
        mobileMapFrame: this.root.querySelector('.stylist-locator__mobile .stylist-locator__map-frame'),
        drawer: this.root.querySelector('[data-element="drawer"]'),
        requestButton: this.root.querySelectorAll('[data-element="request-button"]'),
        requestInputs: {
          name: this.root.querySelectorAll('[data-element="request-name"]'),
          email: this.root.querySelectorAll('[data-element="request-email"]'),
          phone: this.root.querySelectorAll('[data-element="request-phone"]'),
          howDidYouHear: this.root.querySelectorAll('[data-element="request-how-did-you-hear"]'),
        },
        radiusSelects: Array.from(this.root.querySelectorAll('[data-element="radius-select"]')),
        radiusContainers: Array.from(this.root.querySelectorAll('[data-element="radius-container"]')),
        radiusButtons: Array.from(this.root.querySelectorAll('[data-element="radius-button"]')),
        radiusDropdowns: Array.from(this.root.querySelectorAll('[data-element="radius-dropdown"]')),
        radiusButtonTexts: Array.from(this.root.querySelectorAll('[data-element="radius-button-text"]')),
        resetMapButtons: Array.from(this.root.querySelectorAll('[data-element="reset-map"]')),
        toastContainer: this.root.querySelector('[data-element="toast-container"]'),
        filterChips: this.root.querySelector('[data-element="filter-chips"]'),
      }
    }

    init() {
      if (!this.googleMapsKey) {
        console.warn('Stylist Locator: Google Maps API key missing')
        return
      }

      this.refs.heading.textContent = this.heading

      this.setupSearchInputs()
      this.setupRadiusSelect()
      this.setupFilters()
      this.setupViewToggle()
      this.setupFilterDrawer()
      this.setupRequestForm()
      this.setupMapReset()
      this.updateResetButtons()

      window.addEventListener('resize', this.boundResizeHandler)
      this.handleResize()

      loadGoogleMaps(this.googleMapsKey)
        .then((google) => {
          this.state.geocoder = new google.maps.Geocoder()
          if (google.maps.places) {
            this.state.autocompleteService = new google.maps.places.AutocompleteService()
            this.state.placesService = new google.maps.places.PlacesService(document.createElement('div'))
          }
          this.createMap()
          this.initializeUserLocation()
          
          // Ensure map is properly initialized for mobile after a brief delay
          // This allows the DOM to fully render and ensures the map container has dimensions
          if (this.state.windowWidth <= 768 && this.state.map) {
            setTimeout(() => {
              this.updateMapHost()
            }, 300)
          }
        })
        .catch((error) => {
          console.error('Stylist Locator:', error)
          this.toastManager.show('map-error', {
            message: 'Unable to load map. Please try again later.',
            severity: 'error',
            duration: 4000,
          })
        })
    }

    setupSearchInputs() {
      setupSearchInputs(
        this.refs.searchInputs,
        this.refs.searchContainers,
        this.refs.autocompleteDropdowns,
        this.refs.autocompleteContents,
        this.refs.searchButtons,
        this.state,
        {
          onSearchChange: (value) => {
            this.updateResetButtons()
          },
          onPlaceSelect: (placeId) => {
            handlePlaceSelection(placeId, this.state.placesService, {
              onPlaceSelected: (coords, placeName) => {
                this.applySearchCenter(coords, placeName)
                hideAutocompleteDropdown(this.refs.autocompleteDropdowns, this.state)
              },
            })
          },
          onStylistSelect: (emailKey, stylistData) => {
            this.handleStylistSelection(emailKey, stylistData)
          },
          onGeocode: () => {
            triggerGeocode(this.state.search, this.state.geocoder, {
              onGeocodeSuccess: (coords) => {
                this.applySearchCenter(coords)
              },
              onGeocodeError: () => {
                this.toastManager.show('search', {
                  message: 'No address found. Please check the address and try again.',
                  severity: 'error',
                  duration: 3000,
                })
              },
            })
          },
        }
      )
    }

    handleStylistSelection(emailKey, stylistData = null) {
      const { utils = {} } = namespace
      const { decodeKey = (v) => v, parseCoordinate = (v) => v } = utils
      const email = decodeKey(emailKey)

      let stylist = this.state.stylists.find((s) => s.email === email) ||
                     this.state.cachedStylists.find((s) => s.email === email)

      if (!stylist && stylistData) {
        stylist = stylistData
      }

      if (!stylist) {
        console.warn('Stylist not found:', email)
        return
      }

      const searchValue = stylist.name || ''
      this.setSearchInputs(searchValue)
      this.state.search = searchValue

      if (stylist.latitude && stylist.longitude) {
        const coords = {
          lat: parseCoordinate(stylist.latitude),
          lng: parseCoordinate(stylist.longitude),
        }

        this.state.searchCenter = coords
        this.state.referenceCenter = coords

        this.centerMap(coords)

        this.updateRadiusCircle()
        this.fetchStylistsForCurrentRadius().then(() => {
          const foundStylist = this.state.stylists.find((s) => s.email === email) ||
                               this.state.cachedStylists.find((s) => s.email === email)

          if (foundStylist) {
            this.handleListItemClick(emailKey)
            // Check if stylist is in a cluster and expand it
            this.expandClusterIfNeeded(email)
          } else {
            if (!this.state.stylists.find((s) => s.email === email)) {
              this.state.stylists.push(stylist)
              this.updateMarkers()
              this.renderList()
            }
            this.handleListItemClick(emailKey)
            // Check if stylist is in a cluster and expand it
            this.expandClusterIfNeeded(email)
          }
        })
      } else {
        this.handleListItemClick(emailKey)
      }

      hideAutocompleteDropdown(this.refs.autocompleteDropdowns, this.state)
      this.updateResetButtons()
    }

    setupRadiusSelect() {
      setupRadiusSelect(
        this.refs.radiusButtons,
        this.refs.radiusContainers,
        this.refs.radiusDropdowns,
        this.refs.radiusSelects,
        this.refs.radiusButtonTexts,
        this.state,
        {
          onRadiusChange: (value, isDefault) => {
            const center = this.getReferenceCenter()
            if (center && this.state.radius && this.state.map) {
              this.centerMap(center)
            }

            if (center && this.state.radius) {
              this.refreshResults({ updateResetButtons: false })
            } else {
              this.updateRadiusCircle()
              this.state.cachedStylists = []
              this.setStylists([])
            }

            this.updateResetButtons()
          },
        }
      )
    }

    setupFilters() {
      // Find the correct popover for each button (closest one in the DOM)
      const findPopoverForButton = (button) => {
        // Find the closest filters panel
        const filtersPanel = button.closest('[data-element="filters-panel"]')
        if (!filtersPanel) return this.refs.filterPopover // Fallback to first popover
        
        // Find the popover within the same filters panel
        const popover = filtersPanel.querySelector('[data-element="certification-popover"]')
        return popover || this.refs.filterPopover // Fallback to first popover
      }

      this.boundDocumentClick = setupFilters(
        this.refs.certificationRadios,
        this.refs.filterInfoButtons,
        this.refs.filterPopovers, // Pass all popovers
        {
          onCertificationChange: (value) => {
            this.state.certification = value
            this.state.certificationIsDefault = value === 'all'
            this.applyFilters()
            this.updateResetButtons()
          },
          onInfoButtonClick: (button, certValue) => {
            const popover = findPopoverForButton(button)

            // If the same button is clicked again, close the popover
            if (this.state.openPopoverButton === button && this.state.openPopover) {
              this.state.openPopover.style.display = 'none'
              this.state.openPopover = null
              this.state.openPopoverButton = null
              return
            }

            // Close any currently open popover
            if (this.state.openPopover) {
              this.state.openPopover.style.display = 'none'
            }

            // Open the new popover
            showPopover(button, certValue, popover)
            this.state.openPopover = popover
            this.state.openPopoverButton = button
          },
          onPopoverClose: () => {
            // Reset popover state when closed from outside click
            this.state.openPopover = null
            this.state.openPopoverButton = null
          },
        }
      )
    }

    setupViewToggle() {
      this.refs.viewToggleButtons.forEach((button) => {
        button.addEventListener('click', () => {
          const view = button.dataset.view
          if (view && view !== this.state.viewMode) {
            this.state.viewMode = view
            this.renderViewToggle()
            this.renderList()
            this.updateMapHost()
            
            // Trigger map resize when switching to map view to ensure proper rendering
            if (view === 'map' && this.state.map) {
              this.scheduleMapResize(150)
            }
          }
        })
      })
      
      // Set initial view mode on mobile layout
      if (this.refs.mobileLayout) {
        this.refs.mobileLayout.dataset.viewMode = this.state.viewMode
      }
      this.renderViewToggle()
      
      // Ensure map is properly initialized for mobile on first load
      if (this.state.windowWidth <= 768 && this.state.map) {
        setTimeout(() => {
          this.updateMapHost()
        }, 200)
      }
    }

    /**
     * Close all drawers (filters drawer and stylist info drawer).
     */
    closeAllDrawers() {
      if (this.refs.filterDrawer) {
        this.refs.filterDrawer.classList.remove('stylist-locator__drawer--open')
      }
      if (this.refs.drawer) {
        this.refs.drawer.classList.remove('stylist-locator__drawer--open')
      }
    }

    setupFilterDrawer() {
      this.refs.filterToggleButtons.forEach((button) => {
        button.addEventListener('click', () => {
          // Close any other open drawer before opening filters drawer
          this.closeAllDrawers()
          this.refs.filterDrawer.classList.add('stylist-locator__drawer--open')
        })
      })

      if (this.refs.filterDrawerClose) {
        this.refs.filterDrawerClose.addEventListener('click', () => {
          this.refs.filterDrawer.classList.remove('stylist-locator__drawer--open')
        })
      }
    }

    setupRequestForm() {
      this.refs.requestButton.forEach((button) => {
        button.addEventListener('click', () => this.handleRequestClick())
      })

      const validateForm = () => {
        const name = this.state.name || ''
        const email = this.state.email || ''
        const phone = this.state.phone || ''
        const howDidYouHear = this.state.howDidYouHear || ''
        const isValid = name.trim() && email.trim() && phone.trim() && howDidYouHear.trim()
        this.refs.requestButton.forEach((button) => {
          button.disabled = !isValid
        })
      }

      const bindInput = (nodeList, key) => {
        nodeList.forEach((input) => {
          input.addEventListener('input', (event) => {
            this.state[key] = event.target.value
            nodeList.forEach((other) => {
              if (other !== event.target) {
                other.value = event.target.value
              }
            })
            validateForm()
          })
        })
      }

      bindInput(this.refs.requestInputs.name, 'name')
      bindInput(this.refs.requestInputs.email, 'email')
      bindInput(this.refs.requestInputs.phone, 'phone')
      bindInput(this.refs.requestInputs.howDidYouHear, 'howDidYouHear')

      // Initial validation
      validateForm()
    }

    setupMapReset() {
      this.refs.resetMapButtons.forEach((button) => {
        button.addEventListener('click', () => this.resetMapView())
      })
    }

    resetMapView() {
      if (this.state.locationEnabled && this.state.initialCenter) {
        this.state.search = ''
        this.state.searchCenter = null
        this.state.referenceCenter = { ...this.state.initialCenter }
        this.state.radius = this.state.initialRadius
        this.state.radiusIsDefault = true
        this.state.certification = this.state.initialCertification
        this.state.certificationIsDefault = true

        if (this.state.map) {
          this.centerMap(this.state.initialCenter, Number(this.state.radius))
        }

        this.refreshResults({ updateResetButtons: false })
      } else {
        this.state.search = ''
        this.state.searchCenter = null
        this.state.referenceCenter = null
        this.state.radius = null
        this.state.radiusIsDefault = true
        this.state.certification = 'all'
        this.state.certificationIsDefault = true

        if (this.state.map) {
          this.state.map.setCenter(DEFAULT_CENTER)
          this.state.map.setZoom(DEFAULT_ZOOM_OUT)
        }

        if (this.state.radiusCircle) {
          this.state.radiusCircle.setMap(null)
          this.state.radiusCircle = null
        }

        this.state.cachedStylists = []
        this.setStylists([])
      }

      this.clearSelection()

      this.setSearchInputs('')
      this.refs.certificationRadios.forEach((radio) => {
        radio.checked = radio.value === 'all'
      })

      this.refs.radiusSelects.forEach((select) => {
        select.selectedIndex = 0
      })

      this.renderDrawer()
      this.updateResetButtons()
    }

    handleResize() {
      this.state.windowWidth = window.innerWidth
      this.updateMapHost()
      this.renderDrawer()
    }

    updateMapHost() {
      if (!this.state.mapContainer) return
      const isDesktop = this.state.windowWidth > 768
      // On desktop, use desktop host. On mobile, always use mobile host.
      // View mode visibility is controlled by CSS via data-view-mode attribute.
      const targetHost = isDesktop
        ? this.refs.mapHostDesktop
        : this.refs.mapHostMobile

      if (targetHost && targetHost.contains(this.state.mapContainer)) {
        // Even if already in the correct host, trigger resize to ensure map renders properly
        // This is especially important on mobile when switching between views
        this.scheduleMapResize(100)
        return
      }

      if (targetHost) {
        targetHost.innerHTML = ''
        targetHost.appendChild(this.state.mapContainer)
        this.scheduleMapResize(100)
      }
    }

    createMap() {
      const mapContainer = document.createElement('div')
      mapContainer.className = 'stylist-locator__map'
      this.state.mapContainer = mapContainer
      
      // Determine the correct host before appending
      const isDesktop = this.state.windowWidth > 768
      const targetHost = isDesktop
        ? this.refs.mapHostDesktop
        : this.refs.mapHostMobile
      
      // Append to the correct host
      if (targetHost) {
        targetHost.appendChild(mapContainer)
      }

      const center = DEFAULT_CENTER || { lat: 37.1103719, lng: -113.5925891 }
      const map = createMap(mapContainer, center, DEFAULT_ZOOM_OUT || 4)
      this.state.map = map

      // Initialize clusterer after map is ready
      if (initializeClusterer && globalThis.markerClusterer) {
        this.state.markerClusterer = initializeClusterer(map, (eventOrCluster) => {
          this.handleClusterClick(eventOrCluster)
        })
      }

      // Monitor zoom changes to update clustering mode when crossing threshold
      // Use a debounced approach to prevent excessive updates
      let zoomUpdateTimeout = null
      map.addListener('zoom_changed', () => {
        const currentZoom = map.getZoom()
        const shouldCluster = currentZoom < 24 // CLUSTERING_ZOOM_THRESHOLD

        // Clear any pending updates
        if (zoomUpdateTimeout) {
          clearTimeout(zoomUpdateTimeout)
        }

        // Always clear mesh markers on zoom change (similar to regular clusters)
        // This ensures expanded pins don't persist when zooming
        if (this.state.meshMarkers) {
          this.clearMeshMarkers()
        }

        // Only update markers when actually crossing the clustering threshold
        if (this.state.isClusteringEnabled !== shouldCluster) {
          zoomUpdateTimeout = setTimeout(() => {
            this.state.isClusteringEnabled = shouldCluster
            this.updateMarkers()
          }, 150) // Small delay to ensure zoom has settled
        }
      })
      
      // Ensure map is in the correct host after creation
      // Use setTimeout to ensure DOM is ready
      setTimeout(() => {
        this.updateMapHost()
      }, 0)
    }

    findClusterForStylist(stylistEmail) {
      if (!this.state.markerClusterer || !this.state.markers.has(stylistEmail)) {
        return null
      }

      const stylistMarker = this.state.markers.get(stylistEmail)
      const clusters = this.state.markerClusterer.clusters || []

      for (const cluster of clusters) {
        if (cluster.markers && cluster.markers.includes(stylistMarker)) {
          return cluster
        }
      }

      return null
    }

    expandClusterIfNeeded(stylistEmail) {
      const currentZoom = this.state.map ? this.state.map.getZoom() : 0
      const shouldCluster = currentZoom < 24 // CLUSTERING_ZOOM_THRESHOLD from map.js

      if (!shouldCluster || !this.state.markerClusterer) {
        return
      }

      const cluster = this.findClusterForStylist(stylistEmail)
      if (cluster && cluster.markers && cluster.markers.length > 1) {
        // Expand the cluster but skip auto-open since we want to open the specific stylist
        this.handleClusterClick(cluster, { skipAutoOpen: true })

        // After a brief delay to allow mesh creation, trigger click on the stylist's marker
        setTimeout(() => {
          if (this.state.meshMarkerMap && this.state.meshMarkerMap.has(stylistEmail)) {
            const meshMarker = this.state.meshMarkerMap.get(stylistEmail)
            const stylist = this.state.stylists.find((s) => s.email === stylistEmail) ||
                           this.state.cachedStylists.find((s) => s.email === stylistEmail)
            if (meshMarker && stylist) {
              this.handleMarkerClick(stylist, meshMarker)
            }
          }
        }, 100)
      }
    }

    handleClusterClick(eventOrCluster, options = {}) {
      const { skipAutoOpen = false } = options

      // Handle both cases: cluster object directly or event object with cluster property
      let cluster = eventOrCluster
      if (eventOrCluster && eventOrCluster.cluster) {
        cluster = eventOrCluster.cluster
      }

      if (!cluster) {
        console.warn('StylistLocator: Invalid cluster in handleClusterClick', eventOrCluster)
        return
      }
      
      const clusterMarkers = cluster.markers || []
      
      // Get stylists from markers by matching marker instances
      const stylists = []
      for (const clusterMarker of clusterMarkers) {
        // Find stylist by matching marker instance
        for (const [email, stylistMarker] of this.state.markers.entries()) {
          if (stylistMarker === clusterMarker) {
            const stylist = this.state.stylists.find((s) => s.email === email) ||
                           this.state.cachedStylists.find((s) => s.email === email)
            if (stylist) {
              stylists.push(stylist)
            }
            break
          }
        }
      }

      if (stylists.length === 0) return

      // Define threshold for "big cluster" - if too many items, zoom in instead of expanding
      const BIG_CLUSTER_THRESHOLD = 20
      
      // If cluster is too large, zoom in instead of expanding
      if (stylists.length >= BIG_CLUSTER_THRESHOLD) {
        const google = globalThis.google
        const position = cluster.position
        let center
        
        if (position instanceof google.maps.LatLng) {
          center = { lat: position.lat(), lng: position.lng() }
        } else if (position && typeof position.lat === 'function') {
          center = { lat: position.lat(), lng: position.lng() }
        } else if (position && typeof position.lat === 'number') {
          center = { lat: position.lat, lng: position.lng }
        } else {
          return // Invalid position
        }
        
        // Calculate bounds from all stylists in the cluster to determine appropriate zoom
        const bounds = new google.maps.LatLngBounds()
        stylists.forEach((stylist) => {
          if (stylist.latitude && stylist.longitude) {
            bounds.extend(new google.maps.LatLng(stylist.latitude, stylist.longitude))
          }
        })
        
        // If we have valid bounds, fit the map to show all markers
        if (!bounds.isEmpty()) {
          // Add padding to bounds for better visibility
          this.state.map.fitBounds(bounds, {
            padding: 50,
          })
        } else {
          // Fallback: zoom in by 3 levels centered on cluster
          const currentZoom = this.state.map.getZoom()
          const newZoom = Math.min(currentZoom + 3, 15)
          centerMapOnLocation(this.state.map, center, newZoom)
        }
        
        return // Don't create star mesh for large clusters
      }

      // Remove existing mesh markers and lines
      if (this.state.meshMarkers) {
        if (removeStarMesh) {
          removeStarMesh(this.state.meshMarkers)
        }
        this.state.meshMarkers = null
        if (this.state.meshMarkerMap) {
          this.state.meshMarkerMap.clear()
          this.state.meshMarkerMap = null
        }
      }
      
      // Re-add markers to clusterer if they were temporarily removed
      if (this.state.tempRemovedMarkers && this.state.markerClusterer) {
        this.state.markerClusterer.addMarkers(this.state.tempRemovedMarkers)
        this.state.tempRemovedMarkers = null
      }

      // Create star mesh for smaller clusters
      if (createStarMesh) {
        const google = globalThis.google
        const position = cluster.position
        let center
        
        if (position instanceof google.maps.LatLng) {
          center = position
        } else if (position && typeof position.lat === 'function') {
          center = new google.maps.LatLng(position.lat(), position.lng())
        } else if (position && typeof position.lat === 'number') {
          center = new google.maps.LatLng(position.lat, position.lng)
        } else {
          return // Invalid position
        }
        
        // Hide the cluster marker by temporarily removing the cluster's markers
        // This will cause MarkerClusterer to remove the cluster marker
        // We'll store the markers to re-add them later
        if (this.state.markerClusterer && cluster.markers && cluster.markers.length > 0) {
          // Temporarily remove markers from clusterer to hide the cluster
          const tempMarkers = [...cluster.markers]
          this.state.markerClusterer.removeMarkers(tempMarkers)
          // Store markers to re-add when mesh is removed
          this.state.tempRemovedMarkers = tempMarkers
        }
        
        // Initialize mesh marker map in state BEFORE creating mesh
        // This ensures it's available when click handlers fire
        this.state.meshMarkerMap = new Map()
        
        const meshData = createStarMesh(
          this.state.map,
          center,
          stylists,
          (stylist, meshMarker) => {
            // Store mesh marker in state map
            if (this.state.meshMarkerMap) {
              this.state.meshMarkerMap.set(stylist.email, meshMarker)
            }

            // Use setTimeout to ensure marker is fully set up before handling click
            setTimeout(() => {
              this.handleMarkerClick(stylist, meshMarker)
            }, 0)
          }
        )

        // Store mesh data
        this.state.meshMarkers = meshData

        // Store all mesh markers immediately in the map for lookup
        if (meshData && meshData.markers && meshData.stylists && this.state.meshMarkerMap) {
          meshData.markers.forEach((marker, index) => {
            const stylist = meshData.stylists[index]
            if (stylist) {
              this.state.meshMarkerMap.set(stylist.email, marker)
            }
          })
        }

        // Auto-open the first stylist's card after expansion animation completes
        // Skip if this was triggered programmatically (e.g., from clicking a specific stylist in the list)
        if (!skipAutoOpen) {
          const meshStylists = meshData?.stylists || []
          const defaultStylist = meshStylists[0] || null

          if (defaultStylist) {
            setTimeout(() => {
              const defaultMarker = this.state.meshMarkerMap?.get(defaultStylist.email)
              if (defaultMarker) {
                this.state.selectedStylist = defaultStylist
                this.state.showRequest = false
                this.renderList()
                this.renderDrawer()
                this.openInfoWindow(defaultStylist, defaultMarker)
              }
            }, 500) // Wait for expansion animation to complete
          }
        }
      }
    }

    initializeUserLocation() {
      initializeUserLocation(
        (coords) => {
          this.state.userCenter = coords
          this.state.referenceCenter = coords
          this.state.locationEnabled = true
          this.state.initialCenter = { ...coords }
          this.state.hasInitialized = true
          this.state.radius = this.state.initialRadius
          this.state.radiusIsDefault = true

          this.centerMap(coords, Number(this.state.initialRadius) || 25)

          if (this.state.userMarker) {
            this.state.userMarker.setMap(null)
          }
          this.state.userMarker = createUserMarker(this.state.map, coords)

          this.refreshResults()
        },
        () => {
          this.state.locationEnabled = false
          this.state.hasInitialized = true
          this.state.initialCenter = null
          this.updateResetButtons()
        }
      )
    }

    updateRadiusCircle() {
      const center = this.getReferenceCenter()
      const radiusMiles = Number(this.state.radius)
      this.state.radiusCircle = updateRadiusCircle(
        this.state.map,
        this.state.radiusCircle,
        radiusMiles,
        center
      )
    }

    updateMarkers() {
      const selectedEmail = this.state.selectedStylist?.email || null
      
      // Remove mesh markers when updating
      this.clearMeshMarkers()
      
      updateMarkers(
        this.state.map,
        this.state.markers,
        this.state.stylists,
        selectedEmail,
        (stylist, marker) => this.handleMarkerClick(stylist, marker),
        this.state.markerClusterer
      )
    }

    renderList() {
      const selectedEmail = this.state.selectedStylist?.email || null
      renderList(
        this.refs.listCount,
        this.refs.listDesktop,
        this.refs.listMobile,
        this.state.stylists,
        selectedEmail,
        {
          onListItemClick: (emailKey) => {
            this.handleListItemClick(emailKey)
          },
        }
      )

      // Also update mobile list count
      if (this.refs.listCountMobile) {
        const count = this.state.stylists.length
        this.refs.listCountMobile.textContent = `${count.toLocaleString()} ${count === 1 ? 'Stylist' : 'Stylists'}`
      }

      // Toggle gradient visibility based on number of stylists
      const listSection = this.root.querySelector('.stylist-locator__list-section')
      if (listSection) {
        if (this.state.stylists.length > 7) {
          listSection.classList.add('stylist-locator__list-section--has-gradient')
        } else {
          listSection.classList.remove('stylist-locator__list-section--has-gradient')
        }
      }
    }

    handleListItemClick(emailKey) {
      const { decodeKey = (v) => v, parseCoordinate = (v) => v } = namespace.utils || {}
      const email = decodeKey(emailKey)
      const stylist = this.state.stylists.find((item) => item.email === email)
      if (!stylist) return

      // Track list item click
      const position = this.state.stylists.findIndex((s) => s.email === email)
      if (analytics.track) {
        analytics.track('click_list', email, stylist.name, { list_position: position })
      }

      this.state.selectedStylist = stylist
      this.state.showRequest = false
      this.renderList()
      this.renderDrawer()

      // Zoom to stylist location on map
      if (stylist.latitude && stylist.longitude && this.state.map) {
        const coords = {
          lat: parseCoordinate(stylist.latitude),
          lng: parseCoordinate(stylist.longitude),
        }

        this.centerMap(coords)
      }

      // Check if stylist is in a cluster and expand it
      this.expandClusterIfNeeded(email)

      // Only open info window on desktop - mobile uses drawer
      const isDesktop = this.state.windowWidth > 768
      if (isDesktop) {
        this.openInfoWindow(stylist)
      }
    }

    handleMarkerClick(stylist, marker = null) {
      // Track map marker click
      if (analytics.track && stylist?.email) {
        analytics.track('click_map', stylist.email, stylist.name)
      }

      this.state.selectedStylist = stylist
      this.state.showRequest = false
      this.renderList()
      this.renderDrawer()
      // Only open info window on desktop - mobile uses drawer
      const isDesktop = this.state.windowWidth > 768
      if (isDesktop) {
        this.openInfoWindow(stylist, marker)
      }
    }

    openInfoWindow(stylist, marker = null) {
      if (!this.state.map) return
      // Don't open info window on mobile - use drawer instead
      const isDesktop = this.state.windowWidth > 768
      if (!isDesktop) return
      const google = globalThis.google

      if (this.state.openedInfoWindow) {
        this.state.openedInfoWindow.close()
        this.state.openedInfoWindow = null
      }

      let infoWindow = this.state.infoWindows.get(stylist.email)
      if (!infoWindow) {
        infoWindow = new google.maps.InfoWindow({
          content: renderInfoWindowContent(stylist, this.state, {
            getProfileSections,
          }),
          ariaLabel: stylist.name,
        })
        this.state.infoWindows.set(stylist.email, infoWindow)
      } else {
        infoWindow.setContent(renderInfoWindowContent(stylist, this.state, {
          getProfileSections,
        }))
      }

      // Use provided marker, or try to find it from mesh markers, or regular markers
      if (!marker) {
        // Check mesh markers first (for expanded cluster pins)
        if (this.state.meshMarkerMap) {
          marker = this.state.meshMarkerMap.get(stylist.email)
        }
        // Fall back to regular markers
        if (!marker) {
          marker = this.state.markers.get(stylist.email)
        }
      }

      if (marker) {
        infoWindow.open({
          anchor: marker,
          map: this.state.map,
          shouldFocus: false,
        })
        this.state.openedInfoWindow = infoWindow

        // Track stylist view (desktop info window) - only when first viewing, not when opening request form
        if (analytics.track && stylist?.email && !this.state.showRequest) {
          analytics.track('stylist_view', stylist.email, stylist.name, { source: 'desktop' })
        }

        infoWindow.addListener('closeclick', () => {
          this.state.openedInfoWindow = null
          this.resetSelectionUI()
        })
        globalThis.google.maps.event.addListenerOnce(infoWindow, 'domready', () => {
          const { encodeKey = (v) => v } = namespace.utils || {}
          const infoKey = encodeKey(stylist.email || '')
          const wrapper = document.querySelector(`[data-info-root="${infoKey}"]`)?.closest('.stylist-locator__info-wrapper')
          const root = wrapper ? wrapper.querySelector(`[data-info-root="${infoKey}"]`) : document.querySelector(`[data-info-root="${infoKey}"]`)
          if (!root) return

          const googleCloseButton = wrapper?.closest('.gm-style-iw-c')?.querySelector('.gm-ui-hover-effect')
          if (googleCloseButton) {
            googleCloseButton.style.display = 'none'
          }

          const requestButton = root.querySelector('[data-info-request-button]')
          if (requestButton) {
            requestButton.addEventListener('click', () => this.handleRequestClick())
          }
          const closeButton = root.querySelector('[data-info-close]')
          if (closeButton) {
            closeButton.addEventListener('click', () => {
              infoWindow.close()
              this.state.openedInfoWindow = null
              this.resetSelectionUI()
            })
          }
          initializeReadMore(root)
          const backButton = root.querySelector('[data-info-back]')
          if (backButton) {
            backButton.addEventListener('click', () => {
              this.state.showRequest = false
              // Only open info window on desktop - mobile uses drawer
              const isDesktop = this.state.windowWidth > 768
              if (isDesktop) {
                this.openInfoWindow(stylist)
              }
            })
          }
          const validateForm = () => {
            const name = this.state.name || ''
            const email = this.state.email || ''
            const phone = this.state.phone || ''
            const howDidYouHear = this.state.howDidYouHear || ''
            const isValid = name.trim() && email.trim() && phone.trim() && howDidYouHear.trim()
            const requestButton = root.querySelector('[data-info-request-button]')
            if (requestButton) {
              // Only disable button when showing the form, not when viewing stylist info
              const isShowingForm = !!root.querySelector('[data-info-back]')
              requestButton.disabled = isShowingForm && !isValid
            }
          }

          const syncField = (selector, key) => {
            root.querySelectorAll(selector).forEach((input) => {
              input.value = this.state[key] || ''
              input.addEventListener('input', (event) => {
                this.state[key] = event.target.value
                validateForm()
              })
            })
          }
          syncField('[data-info-request-name]', 'name')
          syncField('[data-info-request-email]', 'email')
          syncField('[data-info-request-phone]', 'phone')
          syncField('[data-info-request-how-did-you-hear]', 'howDidYouHear')

          // Initial validation
          validateForm()
          const cleanups = initializeGalleries(root)
          cleanups.forEach((cleanup) => this.galleryCleanups.add(cleanup))
        })
      }
    }

    applyFilters() {
      const byRadius = filterStylistsByRadius(
        this.state.cachedStylists || [],
        Number(this.state.radius),
        this.getReferenceCenter()
      )
      const stylists = filterStylistsByCertification(byRadius, this.state.certification)

      // Sort stylists by role: Elite Artist, Master Technician, Certified Stylist
      const rolePriority = {
        'elite': 1,
        'masters': 2,
        'certified_stylist': 3,
        'default': 4
      }

      // Map role names to categories for certified_salon sorting
      const roleNameToCategory = {
        'Elite Artist': 'elite',
        'Master Technician': 'masters',
        'Certified Stylist': 'certified_stylist',
      }

      // Get reference center for proximity sorting
      const referenceCenter = this.getReferenceCenter()

      const sortedStylists = [...stylists].sort((a, b) => {
        let aCategory = a.roleCategory || determineRoleCategory(a) || 'default'
        let bCategory = b.roleCategory || determineRoleCategory(b) || 'default'

        // For certified_salon, use their actual role for sorting priority
        if (aCategory === 'certified_salon' && a.role) {
          aCategory = roleNameToCategory[a.role] || 'certified_stylist'
        }
        if (bCategory === 'certified_salon' && b.role) {
          bCategory = roleNameToCategory[b.role] || 'certified_stylist'
        }

        const aPriority = rolePriority[aCategory] || 999
        const bPriority = rolePriority[bCategory] || 999

        if (aPriority !== bPriority) {
          return aPriority - bPriority
        }

        // If same role priority, sort by proximity to searched location
        if (referenceCenter && a.latitude && a.longitude && b.latitude && b.longitude) {
          const aDistance = computeDistanceMeters(referenceCenter.lat, referenceCenter.lng, a.latitude, a.longitude)
          const bDistance = computeDistanceMeters(referenceCenter.lat, referenceCenter.lng, b.latitude, b.longitude)
          if (aDistance !== bDistance) {
            return aDistance - bDistance
          }
        }

        // Fallback: sort alphabetically by name
        const aName = (a.name || '').toLowerCase()
        const bName = (b.name || '').toLowerCase()
        return aName.localeCompare(bName)
      })

      if (
        this.state.selectedStylist &&
        !sortedStylists.find((stylist) => stylist.email === this.state.selectedStylist.email)
      ) {
        this.clearSelection()
      }
      this.setStylists(sortedStylists)
      this.renderDrawer()
      this.renderFilterChips()
    }

    getLocationEndpoint() {
      return this.buildEndpoint('/api/search-customers-db')
    }

    getAppointmentEndpoint() {
      return this.buildEndpoint('/api/appointment-request')
    }

    fetchStylistsForCurrentRadius() {
      const locationEndpoint = this.getLocationEndpoint()
      if (!locationEndpoint) return Promise.resolve()
      const center = this.getReferenceCenter()
      const radiusMiles = Number(this.state.radius) || Number(this.defaultRadius) || 25
      if (!center || !radiusMiles || Number.isNaN(radiusMiles)) {
        return Promise.resolve()
      }
      const bounds = computeBoundsFromCenter(center, radiusMiles)
      return this.loadStylists(bounds)
    }

    async loadStylists(bounds) {
      if (this.state.isLoading) return
      this.state.isLoading = true

      const locationEndpoint = this.getLocationEndpoint()
      const stylists = await loadStylists(locationEndpoint, bounds, this.toastManager)
      this.state.cachedStylists = filterStylistsByValidRoles(stylists)
      this.state.isLoading = false
      this.applyFilters()
    }

    getReferenceCenter() {
      if (this.state.referenceCenter) {
        return this.state.referenceCenter
      }
      if (this.state.searchCenter) {
        return this.state.searchCenter
      }
      if (this.state.userCenter) {
        return this.state.userCenter
      }
      return null
    }

    updateResetButtons() {
      const shouldShow = hasActiveFilters(this.state)
      this.refs.resetMapButtons.forEach((button) => {
        if (!button) return
        if (shouldShow) {
          button.classList.add('is-visible')
        } else {
          button.classList.remove('is-visible')
        }
      })
    }

    renderViewToggle() {
      this.refs.viewToggleButtons.forEach((button) => {
        const view = button.dataset.view
        if (view === this.state.viewMode) {
          button.classList.add('stylist-locator__select-button--active')
        } else {
          button.classList.remove('stylist-locator__select-button--active')
        }
      })

      // Update mobile layout data attribute for CSS-driven visibility
      if (this.refs.mobileLayout) {
        this.refs.mobileLayout.dataset.viewMode = this.state.viewMode
      }
    }

    renderDrawer() {
      if (!this.refs.drawer) return
      const isDesktop = this.state.windowWidth > 768
      const shouldOpen = !isDesktop && this.state.selectedStylist

      if (shouldOpen) {
        // Close any other open drawer before opening stylist info drawer
        this.closeAllDrawers()
        
        // Close any open info windows on mobile
        if (this.state.openedInfoWindow) {
          this.state.openedInfoWindow.close()
          this.state.openedInfoWindow = null
        }
        
        // Clean up existing gallery instances before replacing content
        if (this.galleryCleanups && this.galleryCleanups.size) {
          Array.from(this.galleryCleanups).forEach((cleanup) => {
            try {
              cleanup()
            } catch (error) {
              console.error('Stylist Locator: gallery cleanup failed', error)
            }
          })
          this.galleryCleanups.clear()
        }
        
        this.refs.drawer.classList.add('stylist-locator__drawer--open')
        this.refs.drawer.innerHTML = renderDrawerContent(this.state.selectedStylist, this.state, {
          getProfileSections,
        })

        // Track stylist view (mobile drawer) - only when first viewing, not when opening request form
        if (analytics.track && this.state.selectedStylist?.email && !this.state.showRequest) {
          analytics.track('stylist_view', this.state.selectedStylist.email, this.state.selectedStylist.name, { source: 'mobile' })
        }

        const closeButton = this.refs.drawer.querySelector('[data-element="drawer-close"]')
        if (closeButton) {
          closeButton.addEventListener('click', () => {
            this.resetSelectionUI({ closeDrawer: true, renderDrawer: false })
          })
        }

        const backButton = this.refs.drawer.querySelector('[data-element="drawer-back"]')
        if (backButton) {
          backButton.addEventListener('click', () => {
            if (this.state.showRequest) {
              this.state.showRequest = false
              this.renderDrawer()
            } else {
              this.resetSelectionUI({ closeDrawer: true, renderDrawer: false })
            }
          })
        }

        const requestButton = this.refs.drawer.querySelector('[data-element="request-button"]')
        if (requestButton) {
          requestButton.addEventListener('click', () => this.handleRequestClick())
        }

        // Setup form validation for mobile drawer
        const validateDrawerForm = () => {
          const name = this.state.name || ''
          const email = this.state.email || ''
          const phone = this.state.phone || ''
          const howDidYouHear = this.state.howDidYouHear || ''
          const isValid = name.trim() && email.trim() && phone.trim() && howDidYouHear.trim()
          const drawerRequestButton = this.refs.drawer.querySelector('[data-element="request-button"]')
          if (drawerRequestButton) {
            // Only disable button when showing the form, not when viewing stylist info
            const isShowingForm = !!this.refs.drawer.querySelector('[data-element="drawer-back"]')
            drawerRequestButton.disabled = isShowingForm && !isValid
          }
        }

        const syncDrawerField = (selector, key) => {
          this.refs.drawer.querySelectorAll(selector).forEach((input) => {
            input.value = this.state[key] || ''
            input.addEventListener('input', (event) => {
              this.state[key] = event.target.value
              validateDrawerForm()
            })
          })
        }

        // Sync form fields for mobile drawer
        syncDrawerField('[data-element="request-name"]', 'name')
        syncDrawerField('[data-element="request-email"]', 'email')
        syncDrawerField('[data-element="request-phone"]', 'phone')
        syncDrawerField('[data-element="request-how-did-you-hear"]', 'howDidYouHear')

        // Initial validation
        validateDrawerForm()

        initializeReadMore(this.refs.drawer)
        
        // Initialize galleries after drawer is fully rendered
        // Use requestAnimationFrame to ensure DOM is laid out
        requestAnimationFrame(() => {
          const cleanups = initializeGalleries(this.refs.drawer)
          cleanups.forEach((cleanup) => this.galleryCleanups.add(cleanup))
        })
      } else {
        // Close drawer if it shouldn't be open
        if (this.refs.drawer.classList.contains('stylist-locator__drawer--open')) {
          this.refs.drawer.classList.remove('stylist-locator__drawer--open')
        }
      }
    }

    renderFilterChips() {
      if (!this.refs.filterChips) return

      const chips = []
      
      // Certification chip
      if (!this.state.certificationIsDefault && this.state.certification !== 'all') {
        const certificationLabel = this.getCertificationLabel(this.state.certification)
        if (certificationLabel) {
          chips.push({
            type: 'certification',
            label: certificationLabel,
            icon: this.getCertificationIcon(this.state.certification),
          })
        }
      }

      // Radius chip
      if (!this.state.radiusIsDefault && this.state.radius) {
        const radiusLabel = getRadiusLabel(this.state, {
          getRadiusSelect: () => this.refs.radiusSelects[0],
        })
        if (radiusLabel && radiusLabel.toLowerCase() !== 'radius') {
          chips.push({
            type: 'radius',
            label: radiusLabel,
          })
        }
      }

      // Render chips
      if (chips.length === 0) {
        this.refs.filterChips.innerHTML = ''
        return
      }

      this.refs.filterChips.innerHTML = chips.map((chip) => {
        const iconHtml = chip.icon
          ? `<span class="stylist-locator__filter-chip-icon" aria-hidden="true">${chip.icon}</span>`
          : ''
        
        return `
          <div class="stylist-locator__filter-chip" data-filter-type="${chip.type}">
            <div class="stylist-locator__filter-chip-content">
              ${iconHtml}
              <span class="stylist-locator__filter-chip-text">${chip.label}</span>
            </div>
            <button type="button" class="stylist-locator__filter-chip-close" data-filter-type="${chip.type}" aria-label="Remove ${chip.label} filter">
              <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 10 10" fill="none">
                <mask id="mask0_5459_173" style="mask-type:luminance" maskUnits="userSpaceOnUse" x="0" y="0" width="10" height="10">
                  <path d="M4.75 1C6.82083 1 8.5 2.67917 8.5 4.75C8.5 6.82083 6.82083 8.5 4.75 8.5C2.67917 8.5 1 6.82083 1 4.75C1 2.67917 2.67917 1 4.75 1Z" fill="white" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  <path d="M4.7526 4.74967L6.41927 6.41634M4.7526 4.74967L3.08594 3.08301M4.7526 4.74967L3.08594 6.41634M4.7526 4.74967L6.41927 3.08301" stroke="black" stroke-linecap="round" stroke-linejoin="round"/>
                </mask>
                <g mask="url(#mask0_5459_173)">
                  <path d="M9.75 -0.25H-0.25V9.75H9.75V-0.25Z" fill="#B4B4B4"/>
                </g>
              </svg>
            </button>
          </div>
        `
      }).join('')

      // Add click handlers
      this.refs.filterChips.querySelectorAll('.stylist-locator__filter-chip-close').forEach((button) => {
        button.addEventListener('click', () => {
          const filterType = button.dataset.filterType
          if (filterType === 'certification') {
            // Reset to 'all'
            this.refs.certificationRadios.forEach((radio) => {
              if (radio.value === 'all') {
                radio.checked = true
                radio.dispatchEvent(new Event('change', { bubbles: true }))
              }
            })
          } else if (filterType === 'radius') {
            // Reset radius to default
            this.refs.radiusSelects.forEach((select) => {
              select.selectedIndex = 0
              select.dispatchEvent(new Event('change', { bubbles: true }))
            })
          }
        })
      })
    }

    getCertificationLabel(value) {
      const radio = this.refs.certificationRadios.find((r) => r.value === value)
      if (!radio) return null
      const label = radio.closest('.stylist-locator__radio')?.querySelector('label')
      return label ? label.textContent.trim() : null
    }

    getCertificationIcon(value) {
      // Return SVG icon based on certification type - matching the icons used in the filter options
      if (value === 'certified_salon') {
        // Star icon for certified salon (matching liquid file)
        return `<svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 18 17" fill="none">
          <path d="M8.56088 13.7L4.41088 16.2C4.22754 16.3167 4.03588 16.3667 3.83588 16.35C3.63588 16.3333 3.46088 16.2667 3.31088 16.15C3.16088 16.0333 3.04421 15.8877 2.96088 15.713C2.87754 15.5383 2.86088 15.3423 2.91088 15.125L4.01088 10.4L0.335876 7.225C0.169209 7.075 0.0652091 6.904 0.0238758 6.712C-0.0174575 6.52 -0.00512426 6.33267 0.0608757 6.15C0.126876 5.96733 0.226876 5.81733 0.360876 5.7C0.494876 5.58267 0.678209 5.50767 0.910876 5.475L5.76088 5.05L7.63588 0.6C7.71921 0.4 7.84854 0.25 8.02388 0.15C8.19921 0.0499998 8.37821 0 8.56088 0C8.74354 0 8.92254 0.0499998 9.09788 0.15C9.27321 0.25 9.40254 0.4 9.48588 0.6L11.3609 5.05L16.2109 5.475C16.4442 5.50833 16.6275 5.58333 16.7609 5.7C16.8942 5.81667 16.9942 5.96667 17.0609 6.15C17.1275 6.33333 17.1402 6.521 17.0989 6.713C17.0575 6.905 16.9532 7.07567 16.7859 7.225L13.1109 10.4L14.2109 15.125C14.2609 15.3417 14.2442 15.5377 14.1609 15.713C14.0775 15.8883 13.9609 16.034 13.8109 16.15C13.6609 16.266 13.4859 16.3327 13.2859 16.35C13.0859 16.3673 12.8942 16.3173 12.7109 16.2L8.56088 13.7Z" fill="#C6AC78"/>
        </svg>`
      } else if (value === 'elite') {
        // Location pin icon for elite (matching liquid file)
        return `<svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 16 20" fill="none">
          <path d="M8 1.00146e-08C5.89206 -0.00010539 3.86926 0.831759 2.37124 2.31479C0.873231 3.79782 0.0210794 5.81216 0 7.92C0 13.4 7.05 19.5 7.35 19.76C7.53113 19.9149 7.76165 20.0001 8 20.0001C8.23835 20.0001 8.46887 19.9149 8.65 19.76C9 19.5 16 13.4 16 7.92C15.9789 5.81216 15.1268 3.79782 13.6288 2.31479C12.1307 0.831759 10.1079 -0.00010539 8 1.00146e-08ZM8 11C7.30777 11 6.63108 10.7947 6.0555 10.4101C5.47993 10.0256 5.03133 9.47893 4.76642 8.83939C4.50151 8.19985 4.4322 7.49612 4.56725 6.81718C4.7023 6.13825 5.03564 5.51461 5.52513 5.02513C6.01461 4.53564 6.63825 4.2023 7.31718 4.06725C7.99612 3.9322 8.69985 4.00151 9.33939 4.26642C9.97893 4.53133 10.5256 4.97993 10.9101 5.5555C11.2947 6.13108 11.5 6.80777 11.5 7.5C11.5 8.42826 11.1313 9.3185 10.4749 9.97487C9.8185 10.6313 8.92826 11 8 11Z" fill="currentColor"/>
        </svg>`
      } else if (value === 'masters') {
        // Location pin icon with stroke for masters (matching liquid file)
        return `<svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 16 20" fill="none">
          <path d="M8 0.25C10.0421 0.249898 12.0019 1.0555 13.4531 2.49219C14.8137 3.83913 15.6235 5.63877 15.7363 7.54102L15.75 7.92285C15.7488 10.5704 14.0492 13.4058 12.2607 15.6416C10.4784 17.8698 8.65621 19.4443 8.50098 19.5596L8.49414 19.5645L8.4873 19.5703C8.3515 19.6864 8.17865 19.75 8 19.75C7.86605 19.75 7.73533 19.7143 7.62109 19.6475L7.5127 19.5703C7.35982 19.4378 5.53997 17.8609 3.75781 15.6406C1.96283 13.4044 0.251228 10.5698 0.25 7.92285C0.270421 5.88088 1.09568 3.92887 2.54688 2.49219C3.90737 1.1453 5.71496 0.353484 7.61816 0.259766L8 0.25ZM9.43555 4.03516C8.75035 3.75134 7.99595 3.6776 7.26855 3.82227C6.54113 3.96696 5.87308 4.32419 5.34863 4.84863C4.82419 5.37308 4.46696 6.04113 4.32227 6.76855C4.1776 7.49595 4.25134 8.25035 4.53516 8.93555C4.819 9.62064 5.3004 10.2062 5.91699 10.6182C6.5336 11.0301 7.25845 11.25 8 11.25C8.99456 11.25 9.94811 10.8546 10.6514 10.1514C11.3546 9.44811 11.75 8.49456 11.75 7.5C11.75 6.75845 11.5301 6.0336 11.1182 5.41699C10.7062 4.8004 10.1206 4.319 9.43555 4.03516Z" fill="white" stroke="currentColor" stroke-width="0.5"/>
        </svg>`
      } else if (value === 'certified_stylist') {
        // Location pin icon for certified stylist (matching liquid file)
        return `<svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 16 20" fill="none">
          <path d="M8 1.00146e-08C5.89206 -0.00010539 3.86926 0.831759 2.37124 2.31479C0.873231 3.79782 0.0210794 5.81216 0 7.92C0 13.4 7.05 19.5 7.35 19.76C7.53113 19.9149 7.76165 20.0001 8 20.0001C8.23835 20.0001 8.46887 19.9149 8.65 19.76C9 19.5 16 13.4 16 7.92C15.9789 5.81216 15.1268 3.79782 13.6288 2.31479C12.1307 0.831759 10.1079 -0.00010539 8 1.00146e-08ZM8 11C7.30777 11 6.63108 10.7947 6.0555 10.4101C5.47993 10.0256 5.03133 9.47893 4.76642 8.83939C4.50151 8.19985 4.4322 7.49612 4.56725 6.81718C4.7023 6.13825 5.03564 5.51461 5.52513 5.02513C6.01461 4.53564 6.63825 4.2023 7.31718 4.06725C7.99612 3.9322 8.69985 4.00151 9.33939 4.26642C9.97893 4.53133 10.5256 4.97993 10.9101 5.5555C11.2947 6.13108 11.5 6.80777 11.5 7.5C11.5 8.42826 11.1313 9.3185 10.4749 9.97487C9.8185 10.6313 8.92826 11 8 11Z" fill="#798694"/>
        </svg>`
      }
      return null
    }

    async handleRequestClick() {
      if (!this.state.showRequest) {
        // Track form open
        if (analytics.track && this.state.selectedStylist?.email) {
          analytics.track('form_open', this.state.selectedStylist.email, this.state.selectedStylist.name)
        }

        this.state.showRequest = true
        this.renderDrawer()
        // Only open info window on desktop - mobile uses drawer
        const isDesktop = this.state.windowWidth > 768
        if (isDesktop && this.state.selectedStylist) {
          this.openInfoWindow(this.state.selectedStylist)
        }
        return
      }

      // Validate form data before submission
      if (!this.state.name || !this.state.name.trim()) {
        this.showRequestError('Please enter your name')
        return
      }

      if (!this.state.email || !this.state.email.trim()) {
        this.showRequestError('Please enter your email')
        return
      }

      if (!this.state.phone || !this.state.phone.trim()) {
        this.showRequestError('Please enter your phone number')
        return
      }

      if (!this.state.howDidYouHear || !this.state.howDidYouHear.trim()) {
        this.showRequestError('Please tell us how you heard about us')
        return
      }

      if (!this.state.selectedStylist || !this.state.selectedStylist.email) {
        this.showRequestError('No stylist selected')
        return
      }

      // Basic email validation
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      if (!emailRegex.test(this.state.email.trim())) {
        this.showRequestError('Please enter a valid email address')
        return
      }

      // Show loading state
      this.toastManager.show('request', {
        message: 'Sending request...',
        severity: 'info',
        duration: Infinity,
      })

      const appointmentEndpoint = this.getAppointmentEndpoint()
      if (!appointmentEndpoint) {
        this.toastManager.remove('request')
        this.toastManager.show('request', {
          message: 'API endpoint not configured',
          severity: 'error',
          duration: 3000,
        })
        return
      }

      try {
        const response = await fetch(appointmentEndpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'ngrok-skip-browser-warning': 'true',
          },
          body: JSON.stringify({
            name: this.state.name.trim(),
            email: this.state.email.trim(),
            phone: this.state.phone.trim(),
            howDidYouHear: this.state.howDidYouHear.trim(),
            stylistEmail: this.state.selectedStylist.email,
            stylistName: this.state.selectedStylist.name,
          }),
        })

        if (!response.ok) {
          const error = await response.json().catch(() => ({ error: 'Failed to send request' }))
          throw new Error(error.details || error.error || 'Failed to send request')
        }

        const result = await response.json()

        // Track successful form submission (before resetting selectedStylist)
        if (analytics.track && this.state.selectedStylist?.email) {
          analytics.track('form_submit', this.state.selectedStylist.email, this.state.selectedStylist.name, {
            success: true,
            how_did_you_hear: this.state.howDidYouHear.trim(),
          })
        }

        // Reset form and close UI
        this.resetSelectionUI({ closeDrawer: true, closeInfoWindow: true })
        this.state.name = ''
        this.state.email = ''
        this.state.phone = ''
        this.state.howDidYouHear = ''

        // Clear all form inputs
        ;['name', 'email', 'phone', 'howDidYouHear'].forEach((key) => {
          this.refs.requestInputs[key].forEach((input) => {
            input.value = ''
          })
        })

        // Show success message briefly, then redirect to thank you page
        this.toastManager.remove('request')
        this.toastManager.show('request', {
          message: 'Request Sent!',
          severity: 'success',
          duration: 1500,
        })

        // Redirect to thank you page after a brief delay
        setTimeout(() => {
          window.location.href = '/pages/stylist-locator-thank-you'
        }, 500)
      } catch (error) {
        console.error('Request submission error:', error)
        this.toastManager.remove('request')
        this.toastManager.show('request', {
          message: error.message || 'Failed to send request. Please try again.',
          severity: 'error',
          duration: 4000,
        })
      }
    }

    destroy() {
      window.removeEventListener('resize', this.boundResizeHandler)
      if (this.galleryCleanups && this.galleryCleanups.size) {
        Array.from(this.galleryCleanups).forEach((cleanup) => {
          try {
            cleanup()
          } catch (error) {
            console.error('Stylist Locator: gallery cleanup failed', error)
          }
        })
        this.galleryCleanups.clear()
      }
      this.toastManager.clear()
      
      // Clean up mesh markers
      this.clearMeshMarkers()
      
      // Clean up clusterer
      if (this.state.markerClusterer) {
        try {
          this.state.markerClusterer.clearMarkers()
        } catch (error) {
          console.error('Stylist Locator: clusterer cleanup failed', error)
        }
        this.state.markerClusterer = null
      }
      
      if (this.state.map) {
        globalThis.google.maps.event.clearListeners(this.state.map, 'zoom_changed')
      }
      if (this.state.mapContainer && this.state.mapContainer.parentNode) {
        this.state.mapContainer.parentNode.removeChild(this.state.mapContainer)
      }
      if (this.state.radiusCircle) {
        this.state.radiusCircle.setMap(null)
        this.state.radiusCircle = null
      }
      if (this.refs.drawer) {
        this.refs.drawer.classList.remove('stylist-locator__drawer--open')
      }
      if (this.boundDocumentClick) {
        document.removeEventListener('click', this.boundDocumentClick)
        this.boundDocumentClick = null
      }
    }
  }

  function initSection(root) {
    if (controllers.has(root)) {
      return controllers.get(root)
    }
    const controller = new StylistLocatorController(root)
    controllers.set(root, controller)
    return controller
  }

  function destroySection(root) {
    const controller = controllers.get(root)
    if (controller) {
      controller.destroy()
      controllers.delete(root)
    }
  }

  function initAll() {
    document.querySelectorAll(`.${SECTION_CLASS}`).forEach((element) => initSection(element))
  }

  document.addEventListener('DOMContentLoaded', initAll)

  document.addEventListener('shopify:section:load', (event) => {
    const container = event.detail.section
    if (!container) return
    container.querySelectorAll(`.${SECTION_CLASS}`).forEach((element) => initSection(element))
  })

  document.addEventListener('shopify:section:unload', (event) => {
    const container = event.target
    if (!container) return
    container.querySelectorAll(`.${SECTION_CLASS}`).forEach((element) => destroySection(element))
  })
})()
